import { useSelector } from 'react-redux';
import { usePropertiesData } from './usePropertiesData';
import { usePropertyPicturesData } from './usePropertyPicturesData';

/**
 * Hook to get propertyId from Redux config.
 * Returns the propertyId loaded from /api/config endpoint or null if not available.
 *
 * @returns {string|null} The propertyId or null if not available
 */
export const usePropertyId = () => {
    const propertyConfig = useSelector((state) => state.config.propertyConfig);
    return propertyConfig?.propertyId || null;
};

/**
 * Hook to get property data, handling both path-based and domain-based scenarios.
 * In the domain-based scenario, propertyId is null and the API returns a single property.
 *
 * @param {Object}  [options]                       - Optional settings
 * @param {string}  [options.websitePicturesAlbum]  - Album ID to fetch pictures for (e.g. 'home').
 *                                                    When provided, also returns propertyPicturesQueryResults.
 * @returns {Object} Property data and, when options.websitePicturesAlbum is set,
 *                   propertyPicturesQueryResults (a useQuery result for that album).
 */
export const usePropertyData = (options) => {
    const propertyId = usePropertyId();

    const albumId = options?.websitePicturesAlbum || null;

    const propertyResult = usePropertiesData(propertyId);

    // Only enabled when an albumId was requested, so we never fetch all albums here.
    const { picturesQueryResults } = usePropertyPicturesData(propertyId, albumId, !!albumId);

    return {
        ...propertyResult,
        propertyId,
        ...(albumId ? { propertyPicturesQueryResults: picturesQueryResults } : {}),
    };
};
